import Fuse from "fuse.js";
import { useEffect, useRef, useState } from "react";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import {
  getStoredContentFilters,
  storeContentFilters,
} from "../../domains/app/utils/contentFilters";
import { getItemTranslation } from "../../domains/app/utils/getItemTranslation";
import { useApiQuery } from "../../services/api";
import type { PrivateRoom } from "../../domains/room/privateRoom";
import { gaEventSetFilter } from "../../tools/analytics/domains/filter.analytics";
import {
  HIDDEN_SPECIALTY_IDS,
  UNAVAILABLE_SPECIALTIES,
} from "../../tools/utils";
import { Button } from "../ui/Button";
import { SectionTitle } from "../ui/SectionTitle";
import AppCheckbox from "../AppCheckbox";
import CustomIcon from "../CustomIcon";
import { Input } from "../ui/Input";
import { IconSearch, IconX } from "@tabler/icons-react";
import {
  FilterPanelActionBox,
  FilterPanelContent,
  FilterPanelFooter,
  FilterPanelHeader,
  LeftDrawer,
  SelectableItem,
} from "./layout";
import { useTranslation } from "react-i18next";

export default function SpecialtyFilterPanel({
  room,
  isOpen,
  onClose,
  onSave,
}: {
  room: PrivateRoom | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (specialtyIds: string[]) => void;
}) {
  const { activeLang } = useActiveLang();
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState("");
  const specialtiesQuery = useApiQuery("/webapp/public/tags/parents", {
    durable: true,
  });
  const storeSpecialties =
    specialtiesQuery.state === "success" ? specialtiesQuery.body : [];
  const availableSpecialties = storeSpecialties
    .filter((spe) => !HIDDEN_SPECIALTY_IDS.has(spe.id))
    .filter(
      (spe) => !UNAVAILABLE_SPECIALTIES.includes(getItemTranslation(spe, "en"))
    );
  const [selectedSpecialtyIds, setSelectedSpecialtyIds] = useState<string[]>(
    []
  );
  const [memSelectedSpecialtyIds, setMemSelectedSpecialtyIds] = useState<
    string[]
  >([]);
  const isAllSelected =
    selectedSpecialtyIds.length === availableSpecialties.length;
  const [visibleItems, setVisibleItems] = useState(availableSpecialties);
  const sortedItems = [...visibleItems].sort((a, b) => {
    return getItemTranslation(a).localeCompare(getItemTranslation(b));
  });
  const [initialSelectedIds, setInitialSelectedIds] = useState<string[]>([]);
  const scrollerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      const storedFilters = getStoredContentFilters();
      setSelectedSpecialtyIds(storedFilters.specialtyIds);
      setMemSelectedSpecialtyIds(selectedSpecialtyIds);
      setInitialSelectedIds(storedFilters.specialtyIds);
    } else {
      setTimeout(() => {
        setSearchValue("");
      }, 100);
    }
  }, [isOpen]);

  useEffect(() => {
    if (searchValue) {
      const fuse = new Fuse(availableSpecialties, {
        includeScore: true,
        threshold: 0.25,
        keys: [`translations.${activeLang}`],
        ignoreDiacritics: true,
      });
      const searchResults = fuse.search(searchValue).map((res) => res.item);
      setVisibleItems(searchResults);
    } else {
      setVisibleItems(availableSpecialties);
    }
  }, [storeSpecialties, searchValue]);

  function handleToggleSelect(
    spe: (typeof availableSpecialties)[number]
  ): void {
    if (selectedSpecialtyIds.includes(spe.id)) {
      setSelectedSpecialtyIds(
        selectedSpecialtyIds.filter((id) => id !== spe.id)
      );
    } else {
      setSelectedSpecialtyIds([...selectedSpecialtyIds, spe.id]);
    }
  }

  function handleSelectAll() {
    setSelectedSpecialtyIds(availableSpecialties.map((spe) => spe.id));
  }

  function handleUnselectAll() {
    setSelectedSpecialtyIds([]);
  }

  function handleCloseWithoutSaving() {
    onClose();
    setTimeout(() => {
      setSelectedSpecialtyIds(memSelectedSpecialtyIds);
    }, 100);
  }

  function handleSubmit() {
    const specialties = selectedSpecialtyIds
      .map((id) => availableSpecialties.find((spe) => spe.id === id))
      .filter((spe) => spe !== undefined);
    storeContentFilters({
      specialtyIds: selectedSpecialtyIds,
      specialtyUids: specialties.map((spe) => spe.uid),
    });
    if (specialties.length > 0) {
      gaEventSetFilter({
        specialties,
        tags: [],
        roomName: room?.translations.en.name,
      });
    }
    onSave(selectedSpecialtyIds);
    onClose();
  }

  return (
    <LeftDrawer isOpen={isOpen}>
      <FilterPanelHeader>
        <CustomIcon
          className="close-button"
          iconName="close_alt"
          color="var(--color-secondary-bluegrey-7)"
          onClick={handleCloseWithoutSaving}
        />
      </FilterPanelHeader>

      <FilterPanelContent ref={scrollerRef}>
        <span className="info">{t("Filter by specialties")}</span>

        <Input
          value={searchValue}
          placeholder={t("Search")}
          onChange={(e) => {
            setSearchValue(e.target.value);
          }}
          trailing={
            searchValue.length === 0 ? (
              <IconSearch className="size-5 text-ds-slate-400" />
            ) : (
              <button
                type="button"
                aria-label={t("Clear search")}
                className="flex size-8 items-center justify-center rounded-full text-ds-slate-400 hover:text-ds-black"
                onClick={() => {
                  setSearchValue("");
                }}
              >
                <IconX className="size-4" />
              </button>
            )
          }
        />

        {searchValue.length === 0 && (
          <div>
            <SectionTitle title={t("Default")} className="my-4" />

            <FilterPanelActionBox>
              <AppCheckbox
                checked={isAllSelected}
                onChange={(checked) => {
                  if (checked) handleSelectAll();
                  else handleUnselectAll();
                }}
              >
                <span className="text-sm font-bold text-primary-black">
                  {t("Select all")}
                </span>
              </AppCheckbox>

              <div
                className={`clear-all ${selectedSpecialtyIds.length > 0 ? "active" : ""}`}
                onClick={handleUnselectAll}
              >
                <CustomIcon iconName="times-radio" />
                {t("Clear all")}
              </div>
            </FilterPanelActionBox>
          </div>
        )}

        <hr style={{ margin: "16px 0" }} />

        <SectionTitle title={t("Specialties")} className="my-4" />

        {[
          ...sortedItems.filter((spe) => initialSelectedIds.includes(spe.id)),
          ...sortedItems.filter((spe) => !initialSelectedIds.includes(spe.id)),
        ].map((spe) => {
          const isSelected = selectedSpecialtyIds.includes(spe.id);
          return (
            <SelectableItem key={spe.id}>
              <span>{getItemTranslation(spe, activeLang)}</span>
              <Button
                variant={isSelected ? "secondary" : "outline"}
                size="md"
                onClick={() => {
                  handleToggleSelect(spe);
                }}
              >
                {isSelected ? t("Selected") : t("Select")}
              </Button>
            </SelectableItem>
          );
        })}
      </FilterPanelContent>

      <FilterPanelFooter>
        <Button
          variant="primary"
          size="lg"
          className="w-full"
          onClick={handleSubmit}
        >
          {t("Apply")}
        </Button>
      </FilterPanelFooter>
    </LeftDrawer>
  );
}
